import React, { useEffect, useMemo, useRef, useState } from 'react'
import { percentileClim, collectNumbers, boundsToGeometry } from '@carbonplan/zarr-layer'
import type { QueryResult, QueryDataValues } from '@carbonplan/zarr-layer'
import { COLORMAP_NAMES } from '../../../components/ZarrMapViewer/colormaps'
import { useAppStore } from '../store'
import { MultiFilterPills } from './SharedControls'
import { IconMapPin, IconVectorBezier2, IconChartLine } from '@tabler/icons-react'

const inspectRowIconStyle: React.CSSProperties = { flexShrink: 0 }

function smartDecimals(min: number, max: number): number {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return 2
  for (let d = 0; d <= 10; d++) {
    if (min.toFixed(d) !== max.toFixed(d)) return d
  }
  return 10
}

const getRegionMean = (
  result: QueryResult | null,
  fillValue: number
): number | null => {
  if (!result) return null

  let numbers: number[] = []

  for (const [key, value] of Object.entries(result)) {
    if (key === 'dimensions' || key === 'coordinates') continue
    if (!value || typeof value !== 'object') continue

    try {
      numbers = numbers.concat(
        collectNumbers(value as QueryDataValues, fillValue, 0)
      )
    } catch (error) {
      console.error('Error collecting numbers from', key, error)
    }
  }

  if (numbers.length === 0) return null
  const sum = numbers.reduce((acc, value) => acc + value, 0)
  return sum / numbers.length
}

const VIEWPORT_QUERY_MIN_ZOOM = 6

const Controls = () => {
  const datasetId = useAppStore((state) => state.datasetId)
  const datasetModule = useAppStore((state) => state.getDatasetModule())
  const datasetState = useAppStore((state) => state.datasetState)
  const opacity = useAppStore((state) => state.opacity)
  const clim = useAppStore((state) => state.clim)
  const colormap = useAppStore((state) => state.colormap)
  const pointResult = useAppStore((state) => state.pointResult)
  const regionResult = useAppStore((state) => state.regionResult)
  const mapInstance = useAppStore((state) => state.mapInstance)
  const zarrLayer = useAppStore((state) => state.zarrLayer)
  const fillValue =
    zarrLayer?.fillValue ?? datasetModule.fillValue ?? Number.NaN
  const [zoomLevel, setZoomLevel] = useState<number | null>(() =>
    mapInstance ? mapInstance.getZoom() : null
  )

  useEffect(() => {
    if (!mapInstance) {
      setZoomLevel(null)
      return
    }

    const updateZoom = () => {
      try {
        setZoomLevel(mapInstance.getZoom())
      } catch (error) {
        console.error('Failed to read zoom', error)
      }
    }

    updateZoom()
    mapInstance.on?.('zoom', updateZoom)
    mapInstance.on?.('move', updateZoom)

    return () => {
      mapInstance?.off?.('zoom', updateZoom)
      mapInstance?.off?.('move', updateZoom)
    }
  }, [mapInstance])

  const viewportQueryDisabled =
    !mapInstance ||
    !zarrLayer ||
    zoomLevel === null ||
    zoomLevel <= VIEWPORT_QUERY_MIN_ZOOM

  const setOpacity = useAppStore((state) => state.setOpacity)
  const setClim = useAppStore((state) => state.setClim)
  const setColormap = useAppStore((state) => state.setColormap)
  const setRegionResult = useAppStore((state) => state.setRegionResult)
  const setPointResult = useAppStore((state) => state.setPointResult)
  const hoverQueryEnabled = useAppStore((state) => state.hoverQueryEnabled)
  const setHoverQueryEnabled = useAppStore(
    (state) => state.setHoverQueryEnabled
  )
  const timeSeriesResult = useAppStore((state) => state.timeSeriesResult)
  const timeSeriesModeEnabled = useAppStore(
    (state) => state.timeSeriesModeEnabled
  )
  const setTimeSeriesModeEnabled = useAppStore(
    (state) => state.setTimeSeriesModeEnabled
  )
  const setTimeSeriesResult = useAppStore((state) => state.setTimeSeriesResult)
  const timeSeriesWindow = useAppStore((state) => state.timeSeriesWindow)
  const setTimeSeriesWindow = useAppStore((state) => state.setTimeSeriesWindow)
  const timeSeriesAgg = useAppStore((state) => state.timeSeriesAgg)
  const setTimeSeriesAgg = useAppStore((state) => state.setTimeSeriesAgg)
  const timeSeriesUseMeanRange = useAppStore(
    (state) => state.timeSeriesUseMeanRange
  )
  const setTimeSeriesUseMeanRange = useAppStore(
    (state) => state.setTimeSeriesUseMeanRange
  )
  const formatTimeIndex = useAppStore((state) => state.formatTimeIndex)
  // Time-mean state is read by useComputeMean and the TimeSlider; only the
  // pieces the auto-scale checkbox and the "mean-range" range-pill need are
  // kept here.
  const timeMeanResult = useAppStore((state) => state.timeMeanResult)
  const timeMeanStartDate = useAppStore((state) => state.timeMeanStartDate)
  const timeMeanEndDate = useAppStore((state) => state.timeMeanEndDate)

  const [queryInFlight, setQueryInFlight] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const layerConfig = useMemo(
    () => datasetModule.buildLayerProps(datasetState),
    [datasetModule, datasetState]
  )

  useEffect(() => {
    abortRef.current?.abort()
    setPointResult(null)
    setRegionResult(null)
  }, [datasetId, datasetState, setPointResult, setRegionResult])

  const currentVariable = useMemo(() => {
    const layerConfig = datasetModule.buildLayerProps(datasetState)
    return layerConfig.variable ?? datasetModule.variable
  }, [datasetModule, datasetState])

  const pointDisplayValue = useMemo(() => {
    if (!pointResult) return null
    const values = collectNumbers(
      pointResult[currentVariable] as QueryDataValues,
      fillValue
    )
    if (values.length === 0) return null
    const mean = values.reduce((acc, v) => acc + v, 0) / values.length
    return Number.isFinite(mean) ? mean : null
  }, [currentVariable, fillValue, pointResult])

  const regionMean = useMemo(
    () => getRegionMean(regionResult, fillValue),
    [regionResult, fillValue]
  )

  const [autoScaling, setAutoScaling] = useState(false)
  const [climInputs, setClimInputs] = useState<[string, string]>(() => {
    const d = smartDecimals(clim[0], clim[1])
    return [clim[0].toFixed(d), clim[1].toFixed(d)]
  })

  useEffect(() => {
    const d = smartDecimals(clim[0], clim[1])
    setClimInputs([clim[0].toFixed(d), clim[1].toFixed(d)])
  }, [clim])

  // Force `.` as decimal separator regardless of browser locale.
  // Some locales substitute `,` in <input type="number"> which then fails
  // parseFloat. Normalise on read and canonicalise the stored draft.
  const parseNumeric = (s: string) => {
    const cleaned = s.replace(',', '.').replace(/\s/g, '')
    const n = parseFloat(cleaned)
    return Number.isFinite(n) ? n : NaN
  }

  const commitClimInput = (index: 0 | 1, value?: string) => {
    const val = parseNumeric(value ?? climInputs[index])
    if (Number.isFinite(val)) {
      handleClimChange((prev) =>
        index === 0 ? [val, prev[1]] : [prev[0], val]
      )
    } else {
      setClimInputs([String(clim[0]), String(clim[1])])
    }
  }

  const handleClimInputChange = (index: 0 | 1, newValue: string) => {
    const normalised = newValue.replace(',', '.')
    const newNum = parseNumeric(normalised)
    const oldNum = clim[index]
    const isArrowClick =
      Number.isFinite(newNum) && Math.abs(newNum - oldNum) <= 1.01

    if (isArrowClick) {
      commitClimInput(index, normalised)
    } else {
      setClimInputs(
        index === 0 ? [normalised, climInputs[1]] : [climInputs[0], normalised]
      )
    }
  }

  const handleClimChange = (
    next: (prev: [number, number]) => [number, number]
  ) => {
    const resolved = next(clim)
    if (!Array.isArray(resolved) || resolved.length < 2) return
    const [lo, hi] = resolved
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return
    setClim([lo, hi])
  }

  const handleViewportQuery = async () => {
    if (viewportQueryDisabled || queryInFlight) return
    if (!mapInstance || !zarrLayer || !mapInstance.getBounds) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setQueryInFlight(true)
    try {
      const bounds = mapInstance.getBounds()
      if (!bounds) throw new Error('Viewport query is not available')
      const geometry = boundsToGeometry(bounds as any)
      const querySelector = layerConfig.selector

      const result = (await zarrLayer.queryData(geometry, querySelector, {
        signal: controller.signal,
        includeSpatialCoordinates: false,
      })) as QueryResult
      setRegionResult(result)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      console.error('Viewport query failed', error)
      setRegionResult(null)
    } finally {
      setQueryInFlight(false)
    }
  }

  const handleAutoScale = async () => {
    if (autoScaling || !zarrLayer) return

    if (timeMeanResult) {
      const [lo, hi] = percentileClim(timeMeanResult.data)
      setClim([lo, hi])
      return
    }

    if (!mapInstance || !mapInstance.getBounds) return
    setAutoScaling(true)
    try {
      const bounds = mapInstance.getBounds()
      if (!bounds) return
      const geometry = boundsToGeometry(bounds as any)
      const result = (await zarrLayer.queryData(
        geometry,
        layerConfig.selector,
        { includeSpatialCoordinates: false }
      )) as QueryResult
      const numbers = collectNumbers(
        result[currentVariable] as QueryDataValues,
        fillValue
      )
      if (numbers.length > 0) {
        const [lo, hi] = percentileClim(numbers)
        setClim([lo, hi])
      }
    } catch (e) {
      console.error('[auto-scale] error', e)
    } finally {
      setAutoScaling(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: 72,
    border: '1px solid var(--rule)',
    borderRadius: 3,
    fontSize: '0.82rem',
    padding: '2px 4px',
    color: 'var(--navy)',
    background: 'white',
    outline: 'none',
  }

  return (
    <div>
      {/* DISPLAY section — colormap, range, opacity. Highest-touch controls. */}
      <div className="ze-section-label">Display</div>

      {/* Colormap */}
      <div className="ze-control-row">
        <div className="ze-control-label">Colormap</div>
        <select
          value={colormap}
          onChange={(e) => setColormap(e.target.value)}
          style={{ ...inputStyle, width: '100%', cursor: 'pointer', fontFamily: 'var(--card-mono)' }}
        >
          {COLORMAP_NAMES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Range (clim) */}
      <div className="ze-control-row">
        <div className="ze-control-label">Range</div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="text"
            inputMode="decimal"
            value={climInputs[0]}
            onChange={(e) => handleClimInputChange(0, e.target.value)}
            onBlur={() => commitClimInput(0)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitClimInput(0) }}
            style={{ ...inputStyle, width: `${Math.max(4, climInputs[0].length + 2)}ch`, fontFamily: 'var(--card-mono)' }}
          />
          <span style={{ color: 'var(--grey)', fontSize: '0.8rem' }}>–</span>
          <input
            type="text"
            inputMode="decimal"
            value={climInputs[1]}
            onChange={(e) => handleClimInputChange(1, e.target.value)}
            onBlur={() => commitClimInput(1)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitClimInput(1) }}
            style={{ ...inputStyle, width: `${Math.max(4, climInputs[1].length + 2)}ch`, fontFamily: 'var(--card-mono)' }}
          />
        </div>
      </div>

      <div className="ze-control-row ze-control-row--inline">
        <button
          className="ze-btn"
          onClick={handleAutoScale}
          disabled={autoScaling || !zarrLayer}
        >
          {autoScaling ? 'Scaling…' : 'Auto scale'}
        </button>
      </div>

      {/* Opacity */}
      <div className="ze-control-row">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div className="ze-control-label">Opacity</div>
          <span style={{ fontFamily: 'var(--card-mono)', fontSize: '0.78rem', color: 'var(--slate)', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(opacity * 100)}
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={opacity}
          onChange={(e) => setOpacity(parseFloat(e.target.value))}
          className="ze-opacity-slider"
          style={{ width: '100%' }}
        />
      </div>

      <div className="ze-divider" />

      {/* INSPECT section */}
      <div className="ze-section-label">Inspect</div>

      {/* Point — value right-aligned; hover toggle as small icon button */}
      <div className="ze-control-row ze-control-row--inline">
        <IconMapPin size={16} stroke={2} color={hoverQueryEnabled ? 'var(--teal)' : 'var(--grey)'} style={inspectRowIconStyle} />
        <span className="ze-control-label" style={{ flex: 1 }}>Point</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {pointDisplayValue !== null ? (
            <>
              <span style={{ fontFamily: 'var(--card-mono)', fontSize: '0.82rem', color: 'var(--navy)', fontVariantNumeric: 'tabular-nums' }}>
                {pointDisplayValue.toFixed(4)}
              </span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey)', fontSize: 12, lineHeight: 1, padding: 0 }} onClick={() => setPointResult(null)} title="Clear">✕</button>
            </>
          ) : (
            <span style={{ fontSize: '0.78rem', color: 'var(--grey)' }}>—</span>
          )}
          <button
            title={hoverQueryEnabled ? 'Hover query on' : 'Hover query off'}
            onClick={() => setHoverQueryEnabled(!hoverQueryEnabled)}
            style={{
              background: hoverQueryEnabled ? 'var(--navy)' : 'transparent',
              border: '1px solid var(--rule)',
              borderRadius: 3,
              cursor: 'pointer',
              color: hoverQueryEnabled ? 'white' : 'var(--slate)',
              fontSize: 9,
              padding: '1px 5px',
              lineHeight: '16px',
              fontFamily: 'var(--card-sans)',
            }}
          >
            hover
          </button>
        </div>
      </div>

      {/* Region mean — shows value or zoom hint; query button when available */}
      <div className="ze-control-row ze-control-row--inline">
        <IconVectorBezier2 size={16} stroke={2} color="var(--grey)" style={inspectRowIconStyle} />
        <span className="ze-control-label" style={{ flex: 1 }}>Region mean</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {regionMean !== null ? (
            <>
              <span style={{ fontFamily: 'var(--card-mono)', fontSize: '0.82rem', color: 'var(--navy)', fontVariantNumeric: 'tabular-nums' }}>
                {regionMean.toFixed(4)}
              </span>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--grey)', fontSize: 12, lineHeight: 1, padding: 0 }} onClick={() => setRegionResult(null)} title="Clear">✕</button>
            </>
          ) : viewportQueryDisabled ? (
            <span style={{ fontSize: '0.78rem', color: 'var(--grey)' }}>zoom in</span>
          ) : (
            <button className="ze-btn" onClick={handleViewportQuery} disabled={queryInFlight}>
              {queryInFlight ? 'querying…' : 'query'}
            </button>
          )}
        </div>
      </div>

      {/* Time series — on/off chip + sub-controls when enabled */}
      <div className="ze-control-row ze-control-row--inline">
        <IconChartLine size={16} stroke={2} color={timeSeriesModeEnabled ? 'var(--teal)' : 'var(--grey)'} style={inspectRowIconStyle} />
        <span className="ze-control-label" style={{ flex: 1 }}>
          Time series
          {timeSeriesModeEnabled && timeSeriesResult === null && (
            <span style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--grey)', fontWeight: 400 }}>click map</span>
          )}
        </span>
        <button
          className={`ze-chip${timeSeriesModeEnabled ? ' ze-chip--active' : ''}`}
          onClick={() => {
            if (timeSeriesModeEnabled) { setTimeSeriesModeEnabled(false); setTimeSeriesResult(null) }
            else setTimeSeriesModeEnabled(true)
          }}
        >
          {timeSeriesModeEnabled ? 'on' : 'off'}
        </button>
      </div>

      {timeSeriesModeEnabled && (
        <>
          {formatTimeIndex && timeMeanStartDate && timeMeanEndDate && (
            <div className="ze-control-row">
              <div className="ze-control-label">Range</div>
              <div style={{ flex: 1 }}>
                <MultiFilterPills
                  values={{ window: !timeSeriesUseMeanRange, 'mean range': timeSeriesUseMeanRange }}
                  onChange={(key) => {
                    if (key === 'window') setTimeSeriesUseMeanRange(false)
                    if (key === 'mean range') setTimeSeriesUseMeanRange(true)
                  }}
                />
              </div>
            </div>
          )}
          <div className="ze-control-row">
            <div className="ze-control-label">Aggregate</div>
            <div style={{ flex: 1 }}>
              <MultiFilterPills
                values={{
                  none: timeSeriesAgg === 'none',
                  monthly: timeSeriesAgg === 'monthly',
                  yearly: timeSeriesAgg === 'yearly',
                }}
                onChange={(key) => {
                  if (key === 'none') { setTimeSeriesAgg('none'); setTimeSeriesWindow(30) }
                  if (key === 'monthly') { setTimeSeriesAgg('monthly'); setTimeSeriesWindow(365) }
                  if (key === 'yearly') { setTimeSeriesAgg('yearly'); setTimeSeriesWindow('all') }
                }}
              />
            </div>
          </div>
          {!timeSeriesUseMeanRange && timeSeriesAgg !== 'yearly' && (
            <div className="ze-control-row">
              <div className="ze-control-label">Window</div>
              <div style={{ flex: 1 }}>
                {timeSeriesAgg === 'none' ? (
                  <MultiFilterPills
                    values={{ '1w': timeSeriesWindow === 7, '2w': timeSeriesWindow === 14, '1m': timeSeriesWindow === 30, '3m': timeSeriesWindow === 90, '6m': timeSeriesWindow === 180, '1y': timeSeriesWindow === 365 }}
                    onChange={(key) => {
                      if (key === '1w') setTimeSeriesWindow(7)
                      if (key === '2w') setTimeSeriesWindow(14)
                      if (key === '1m') setTimeSeriesWindow(30)
                      if (key === '3m') setTimeSeriesWindow(90)
                      if (key === '6m') setTimeSeriesWindow(180)
                      if (key === '1y') setTimeSeriesWindow(365)
                    }}
                  />
                ) : (
                  <MultiFilterPills
                    values={{ '3m': timeSeriesWindow === 90, '1y': timeSeriesWindow === 365, '5y': timeSeriesWindow === 1825, all: timeSeriesWindow === 'all' }}
                    onChange={(key) => {
                      if (key === '3m') setTimeSeriesWindow(90)
                      if (key === '1y') setTimeSeriesWindow(365)
                      if (key === '5y') setTimeSeriesWindow(1825)
                      if (key === 'all') setTimeSeriesWindow('all')
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}

    </div>
  )
}

export default Controls
